import { LayoutWithHeaderAndFooter } from '@/common/components/layouts/layoutWithHeaderAndFooter';
import Seo from '@/common/components/layouts/seo';
import { getFloatingNavInset } from '@/common/components/layouts/bottomNavigation/floatingBottomNavigation';
import useIsNewAppShell from '@/common/hooks/useIsNewAppShell';
import { useIsomorphicLayoutEffect } from '@/common/hooks/useIsomorphicLayoutEffect';
import { isNewAppShell } from '@/common/utils/newAppShell';
import { ChatRecoveryDock } from '@/modules/hami/chatRecovery/RecoveryDock';
import { ChatAssistantDrawer } from '@/modules/hami/components/chatAssistantDrawer';
import { ChatAssistantPanel } from '@/modules/hami/components/chatAssistantPanel';
import { RouteChatsSkeleton } from '@/modules/hami/components/nativeChatsSkeleton';
import { decideHamiFront, parseForce, willUseNativeFront } from '@/modules/hami/hamiFront';
import { getHamiChatIdFromRoute, isHamiMainChatsRoute } from '@/modules/hami/hamiRoutes';
import LegacyIframeChatsPage from '@/modules/hami/legacyIframeChatsPage';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { ReactElement, ReactNode, useState } from 'react';

// The messenger's shared shell (auth, user, presence, calls); its first frame is the same skeleton
// the page shows before it, so nothing jumps.
const loadHamiApp = () => import('@/modules/hami/app/HamiApp');
// In the browser, the moment this page's code runs: download the shell and start its start-up
// work (session check, saved chats) at once, side by side with the screen's own chunk.
// Only where the native messenger will be shown (the new app, or forced for QA): the website and older
// app builds keep the old Hami iframe and must not pay for a messenger they never open.
if (typeof window !== 'undefined' && willUseNativeFront()) {
  loadHamiApp();
  import('@/modules/hami/app/boot').then(boot => boot.startHamiBoot());
}
const HamiApp = dynamic(loadHamiApp, {
  ssr: false,
  loading: () => <RouteChatsSkeleton />,
});

/** The messenger's own path for the current URL: «/_/chats/12/» → «/chats/12». */
const messengerRoute = (asPath: string) => asPath.split(/[?#]/)[0].replace(/^\/_(?=\/|$)/, '').replace(/\/+$/, '') || '/';

/**
 * Layout of every messenger page (pages/_/chats, pages/_/chats/[id], pages/_/stories, …). It stays
 * mounted while the user moves between them (Next keeps a page's layout when the next page has the
 * same one), so the messenger's providers and connections are not rebuilt on each screen.
 *
 * The native messenger is for the new Android app; the website and older app builds keep the old
 * Hami site in an iframe. ?native=1 / ?native=0 / ?legacy=1 force a side (QA).
 */
export function HamiLayout({ config, children }: { config?: Record<string, unknown>; children: ReactNode }) {
  const router = useRouter();
  const isFloatingNav = useIsNewAppShell();

  // The new app is known from the <html> flag its head script sets, read before the first paint.
  const [newApp, setNewApp] = useState<boolean | null>(null);
  useIsomorphicLayoutEffect(() => setNewApp(isNewAppShell()), []);
  const force = router.query.legacy === '1' ? false : parseForce(router.query.native, process.env.NEXT_PUBLIC_HAMI_NATIVE_FRONT);
  const front = router.isReady ? decideHamiFront({ force, newApp }) : 'pending';

  if (front === 'legacy') return <LegacyIframeChatsPage config={config} />;

  const route = messengerRoute(router.asPath);
  const chatId = getHamiChatIdFromRoute(route);
  const showBottomNavigation = isHamiMainChatsRoute(route);
  // New app: the floating nav overlays the page, so the messenger runs full height under it and pads its list.
  const underNav = isFloatingNav && showBottomNavigation;

  return (
    <>
      {/* Android Chrome: the keyboard shrinks the page (like Telegram) instead of panning the header off-screen */}
      <Head>
        <meta
          name="viewport"
          content="viewport-fit=cover, width=device-width, initial-scale=1.0, minimum-scale=1.0, maximum-scale=1.0, interactive-widget=resizes-content"
        />
      </Head>
      <LayoutWithHeaderAndFooter
        {...config}
        shouldShowPromoteApp={false}
        showFooter={false}
        showHeader={false}
        showBottomNavigation={showBottomNavigation}
        // always marked: the CSS that drops the docked bar's padding only applies in the new app
        // (html[data-floating-bottom-nav]), and so it already holds in the server's first frame
        fullBleedUnderBottomNav={showBottomNavigation}
        className="!h-svh !min-h-svh !max-h-svh:"
      >
        <Seo title={'گفت‌وگو‌ها'} noIndex />
        <div
          className="relative flex md:h-[calc(100vh-80px)] h-full w-full flex-grow flex-col items-center justify-center overflow-hidden"
          style={{ ['--hami-bottom-inset' as string]: `${underNav ? getFloatingNavInset() : 0}px` }}
        >
          <ChatAssistantDrawer
            isActive={!!chatId}
            chatId={chatId}
            panelContent={({ isOpen, hasChatWidget, isWidgetsLoading }) => (
              <ChatAssistantPanel isOpen={isOpen} chatId={chatId} hasChatWidget={hasChatWidget} isWidgetsLoading={isWidgetsLoading} />
            )}
          >
            {front === 'native' ? <HamiApp>{children}</HamiApp> : <RouteChatsSkeleton />}
          </ChatAssistantDrawer>
          <ChatRecoveryDock chatId={chatId} active={!!chatId} onInteractiveChange={() => undefined} />
        </div>
      </LayoutWithHeaderAndFooter>
    </>
  );
}

/** getLayout for the messenger's pages: one HamiLayout instance across all of them. */
export const getHamiLayout = (page: ReactElement) => <HamiLayout config={page.props?.config}>{page}</HamiLayout>;
